import axios, {AxiosResponse} from 'axios';
import {BASE_URL} from '../globals/Constants';
import {setCategoryString} from './Helper';
import {IContact} from '../interfaces/IContact';

/**
 * Standard Spring ProblemDetail + your custom backend fields
 */
export interface ProblemDetail {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    instance?: string;

    vehicleId?: string;
    vedocStatusCode?: string;
    vedocMessage?: string;
    vedocReason?: string;
    vedocOrigin?: string;
    vedocTraceId?: string;
    vedocErrorDetails?: unknown;
}

/**
 * Main backend response of GET /vehicle/{id}
 */
export interface VedocApiResponse {
    fin: string;
    vin: string | null;

    baseInfo?: {
        cvBodyType?: string;
        finStamping?: boolean;
        plant?: string;
        status?: string;
        productionNumber?: string;
        orderNumber?: string;
        checkDigit?: string;
        productSeries?: string;
        vehicleModelDesignation?: string;
    };

    codeInfo?: {
        equipmentCodes?: Array<{
            code: string;
            codeType?: string;
            productGroupIndication?: string;
        }>;
        paint?: Array<{
            code: string;
            usage?: string;
        }>;
    };

    majorAssembliesBaseInfo?: {
        afterTreatmentSystem?: {
            variantInfoAvailable?: boolean;
            dataCardAvailable?: boolean;
        };

        axles?: Array<{
            id?: string;
            objectNumberVariant?: string;
            dataCardAvailable?: boolean;
            typeOf?: string;
            variantInfoAvailable?: boolean;
        }>;

        cab?: {
            id?: string;
            variantInfoAvailable?: boolean;
            dataCardAvailable?: boolean;
        };

        engine?: {
            id?: string;
            objectNumberVariant?: string;
            objectNumber?: string;
            variantInfoAvailable?: boolean;
            dataCardAvailable?: boolean;
        };

        transmission?: {
            id?: string;
            objectNumberVariant?: string;
            variantInfoAvailable?: boolean;
            dataCardAvailable?: boolean;
        };

        frontbox?: {
            variantInfoAvailable?: boolean;
        };
    };

    salesData?: {
        authorizedAgent?: {
            validFrom?: string;
        };
        customerServiceData?: {
            validSince?: string;
            largeScaleTestVehicle?: boolean;
            customerTestVehicle?: boolean;
            archiveIndicator?: boolean;
        };
        productDates?: {
            validSince?: string;
            shipmentDate?: string;
            technicalApprovalDate?: string;
        };
    };
}

/**
 * Adjust this shape if your /oilfinder endpoint differs.
 */
export interface OilFinderModelAndSheetsResponse {
    body: {
        prioritySheet: string | null;
        otherSheets: string | null;
        vehicleModel: string;
        engineName: string;
    };
}

export class ApiError extends Error {
    problem: ProblemDetail | null;

    constructor(problem: ProblemDetail | null) {
        super(problem?.detail || problem?.title || 'Unexpected API error');
        this.name = 'ApiError';
        this.problem = problem;
    }
}

export const getSheetsByCategory = (category: string) => {
    const categoryText = setCategoryString(category);
    return axios.get(`${BASE_URL}category/${categoryText}`);
};

export const getAllSheets = () => {
    return axios.get(`${BASE_URL}allSheetsByCategory`);
};

export const getAllData = () => {
    return axios.get(`${BASE_URL}all`);
};

export const getSheetNumbersByCategory = (category: string) => {
    const categoryText = setCategoryString(category);
    return axios.get(`${BASE_URL}categorySheetNumbers/${categoryText}`);
};

export const getSheetByNumber = (sheetNumber: string) => {
    const normalizedSheetNumber = sheetNumber.replaceAll('_', '%20');
    return axios.get(`${BASE_URL}sheet/${normalizedSheetNumber}`);
};

export const getSearchResults = (text: string) => {
    return axios.get(`${BASE_URL}search/${text}`);
};

export const getProductsById = (productId: string) => {
    return axios.get(`${BASE_URL}product/${productId}`);
};

export async function sendContact(contactData: IContact) {
    return axios.post(`${BASE_URL}contact`, contactData);
}

/**
 * New backend contract:
 * success => returns VedocApiResponse directly
 * error   => throws ApiError containing backend ProblemDetail
 */

export const getOilFinderResults = async (
    text: string
): Promise<VedocApiResponse> => {
    try {
        const response: AxiosResponse<VedocApiResponse> = await axios.get(
            `${BASE_URL}vehicle/${encodeURIComponent(text)}`
        );

        return response.data as VedocApiResponse;
    } catch (error: unknown) {
        let problem: ProblemDetail = {
            title: 'Request failed',
            detail: 'Unexpected error while loading vehicle data.',
            status: 500,
        };

        if (axios.isAxiosError(error)) {
            if (error.response?.data) {
                problem = error.response.data as ProblemDetail;
            } else {
                problem = {
                    title: 'Request failed',
                    detail: error.message || 'Unexpected error while loading vehicle data.',
                    status: error.response?.status ?? 500,
                };
            }
        }

        throw new ApiError(problem);
    }
};

export const getOilFinderModelandSheets = (
    engineCode: string,
    modelCode: string,
    equipmentCodes: string[]
) => {
    const equipmentCodeQuery = equipmentCodes
    .map((code: string) => code.trim())
    .join(',');

    return axios.get<OilFinderModelAndSheetsResponse>(
        `${BASE_URL}oilfinder/${engineCode}/${modelCode}`,
        {
            params: {
                equipmentCode: equipmentCodeQuery,
            },
        }
    );
};